/**
 * Point-pair weight semantics — the single source of truth.
 *
 * - Ignore (-1): excluded from the fit; no numeric residual.
 * - Free (0.01): excluded from the fit; residual is still displayed.
 * - Unity (1): a normal observation.
 * - Fixed (100): fixed-point handling. Exactly two Fixed points define a
 *   constrained transform; one defines a minimally constrained transform.
 *
 * Classification uses thresholds rather than equality so that legacy or
 * hand-edited weights still land in a predictable category.
 */

export const WEIGHT = {
  ignore: -1,
  free: 0.01,
  unity: 1,
  fixed: 100,
} as const;

export type WeightStatus = keyof typeof WEIGHT;
export type PointWeight = (typeof WEIGHT)[WeightStatus];

/**
 * `shortLabel` doubles as the map keyboard shortcut (documented in the
 * getting-started guide): U = Unity, F = Free, X = Fixed, I = Ignore.
 */
export const WEIGHT_OPTIONS: ReadonlyArray<{
  status: WeightStatus;
  value: PointWeight;
  label: string;
  shortLabel: string;
  description: string;
}> = [
  {
    status: 'unity',
    value: WEIGHT.unity,
    label: 'Unity',
    shortLabel: 'U',
    description: 'Normal observation included in the fit',
  },
  {
    status: 'fixed',
    value: WEIGHT.fixed,
    label: 'Fixed',
    shortLabel: 'X',
    description: 'Held fixed; one or two Fixed points constrain the transform',
  },
  {
    status: 'free',
    value: WEIGHT.free,
    label: 'Free',
    shortLabel: 'F',
    description: 'Excluded from the fit; residual still shown',
  },
  {
    status: 'ignore',
    value: WEIGHT.ignore,
    label: 'Ignore',
    shortLabel: 'I',
    description: 'Excluded from the fit; no residual',
  },
];

export const isIgnored = (weight: number): boolean => weight < 0;
export const isFree = (weight: number): boolean => weight >= 0 && weight < 1;
export const isFixed = (weight: number): boolean => weight >= 100;
/** Unity or Fixed: the pair participates in the least-squares fit. */
export const isInFit = (weight: number): boolean => weight >= 1;
/** Free, Unity, or Fixed: the pair has a residual worth displaying. */
export const hasResidual = (weight: number): boolean => weight >= 0;

export const getWeightStatus = (weight: number): WeightStatus => {
  if (isIgnored(weight)) return 'ignore';
  if (isFixed(weight)) return 'fixed';
  if (isFree(weight)) return 'free';
  return 'unity';
};

export const getWeightLabel = (weight: number): string =>
  WEIGHT_OPTIONS.find((option) => option.status === getWeightStatus(weight))!
    .label;

/**
 * Snap an untrusted weight (file import, project file, autosave) to one of
 * the four supported values. Returns null when the value is not a finite
 * number, so callers can reject or default it explicitly.
 */
export const normalizeWeight = (value: unknown): PointWeight | null => {
  const numeric =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim() !== ''
        ? Number(value)
        : NaN;
  if (!Number.isFinite(numeric)) return null;
  return WEIGHT[getWeightStatus(numeric)];
};
